import { Injectable, isDevMode, inject } from '@angular/core';
import { merge, Observable, of, share, Subject, take, timer } from 'rxjs';
import { Message, MessageType, Topic } from '../../model/model';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { distinctUntilChanged, filter, map, retry, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BrowserContext } from '../../browser-context.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private readonly ctx = inject(BrowserContext);

  private socket$?: WebSocketSubject<Message>;
  public readonly onlineStatus$: Observable<boolean>;

  constructor() {
    if (this.ctx.isBrowser) {
      const openSubject$ = new Subject<Event>();
      const closeSubject$ = new Subject<CloseEvent>();

      this.onlineStatus$ = merge(
        of(false),
        openSubject$.pipe(map(() => true)),
        closeSubject$.pipe(map(() => false))
      ).pipe(distinctUntilChanged(), shareReplay(1));

      this.onlineStatus$.subscribe(value => isDevMode() && console.log('Websocket Connection established:', value));
      let socketUrl = environment.endpoints.webSocket;
      if (!socketUrl.startsWith('ws://') && !socketUrl.startsWith('wss://')) {
        socketUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${socketUrl}`;
      }
      this.socket$ = webSocket<Message>({
        url: `${socketUrl}`,
        openObserver: openSubject$,
        closeObserver: closeSubject$,
      });
    } else {
      this.onlineStatus$ = of(false);
    }
  }

  public getUpdateStream(topic: Topic) {
    let subscribeMessageId: string | undefined;
    const socket$ = this.socket$!.multiplex(
      () => {
        subscribeMessageId = crypto.randomUUID();
        return { id: subscribeMessageId, messageType: 'subscribe', topic, value: topic };
      },
      () => ({ id: crypto.randomUUID(), messageType: 'unsubscribe', topic, value: topic }),
      value => value.reply === subscribeMessageId!
    ).pipe(share());
    return socket$.pipe(
      // wait for the confirm event
      filter(value => value.messageType === MessageType.Confirm),
      take(1),
      // switch to the stream
      switchMap(() => {
        if (isDevMode()) {
          console.log('Websocket Subscription Confirmed', topic);
        }
        return socket$;
      }),

      // in case of a connection abort retry
      retry({
        resetOnSuccess: true,
        delay: (error, retryCount) => this.backoff(error, retryCount),
      }),
      share()
    );
  }

  sendMessage(msg: Message) {
    this.socket$?.next(msg);
  }

  private backoff(err: unknown, retryCount: number) {
    const interval = 500;
    const delay = Math.min(Math.pow(2, retryCount - 1) * interval, 60 * 1000);
    if (isDevMode()) {
      console.warn(`Websocket connection retry nr ${retryCount} in ${delay}ms due to error.`, err);
    }
    return timer(delay);
  }
}
